/** Fixed updates are accumulated independently of display refresh; catch-up work is bounded. */
export class Loop {
  private readonly stepMs: number;
  private readonly update: (stepIndex: number) => void;
  private readonly render: (alpha: number, fps: number) => void;
  private raf = 0;
  private last = 0;
  private acc = 0;
  private stepIndex = 0;
  private frames = 0;
  private framesAt = 0;
  private fps = 0;
  private static readonly MAX_STEPS_PER_FRAME = 5;
  private static readonly MAX_FRAME_MS = 250;
  constructor(
    stepHz: number,
    update: (stepIndex: number) => void,
    render: (alpha: number, fps: number) => void,
  ) {
    this.stepMs = 1000 / stepHz;
    this.update = update;
    this.render = render;
  }
  start(): void {
    this.last = performance.now();
    this.framesAt = this.last;
    const tick = (now: number): void => {
      this.raf = requestAnimationFrame(tick);
      let delta = now - this.last;
      this.last = now;
      if (delta > Loop.MAX_FRAME_MS) delta = Loop.MAX_FRAME_MS;
      this.acc += delta;
      let steps = 0;
      while (this.acc >= this.stepMs && steps < Loop.MAX_STEPS_PER_FRAME) {
        this.update(this.stepIndex++);
        this.acc -= this.stepMs;
        steps++;
      }
      if (steps === Loop.MAX_STEPS_PER_FRAME && this.acc > this.stepMs) {
        this.acc = this.acc % this.stepMs;
      }
      this.frames++;
      if (now - this.framesAt >= 1000) {
        this.fps = Math.round((this.frames * 1000) / (now - this.framesAt));
        this.frames = 0;
        this.framesAt = now;
      }
      this.render(this.acc / this.stepMs, this.fps);
    };
    this.raf = requestAnimationFrame(tick);
  }
  stop(): void {
    cancelAnimationFrame(this.raf);
  }
  get currentFps(): number {
    return this.fps;
  }
}
